import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Target, Lock, ArrowRight, ExternalLink } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { CodyIcon } from '../components/CodyLogo';
import { useAuth } from '../lib/auth';

function IntentBadge({ score }) {
  let cls = 'intent-low';
  if (score >= 70) cls = 'intent-high';
  else if (score >= 40) cls = 'intent-medium';
  return (
    <span className={`${cls} text-xs font-semibold px-2.5 py-1 rounded-full inline-block`} style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
      {score}
    </span>
  );
}

function StatusBadge({ status }) {
  const cls = `status-${status.toLowerCase()}`;
  return <Badge variant="outline" className={`text-xs border ${cls} capitalize`}>{status}</Badge>;
}

export default function LeadPreview() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const stored = sessionStorage.getItem('scanResults');
    if (!stored) {
      navigate('/');
      return;
    }
    setData(JSON.parse(stored));
  }, [navigate]);

  if (!data) return null;

  const visibleLeads = data.leads.slice(0, 3);
  const blurredLeads = data.leads.slice(3, 8);
  const remaining = Math.max(0, data.total_leads - 3);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <header className="border-b border-[#E6E8EE] bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <CodyIcon size={32} />
            <span className="font-semibold text-[15px]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Cody</span>
          </div>
          {!user && (
            <Button onClick={() => navigate('/signup')} className="h-9 rounded-lg text-sm" style={{ background: 'linear-gradient(180deg, #FF6A2A 0%, #FF4500 100%)' }}>
              Sign Up
            </Button>
          )}
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Product info */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-semibold mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            We found Reddit discussions about <span className="text-[#FF4500]">{data.product_name}</span>
          </h1>
          <p className="text-[#5B667A]">{data.product_description}</p>
          <div className="flex flex-wrap gap-2 mt-3">
            {data.keywords.slice(0, 8).map((kw, i) => (
              <Badge key={i} variant="secondary" className="text-xs bg-[#F6F7F9] text-[#5B667A]">{kw}</Badge>
            ))}
            {data.keywords.length > 8 && (
              <Badge variant="secondary" className="text-xs bg-[#F6F7F9] text-[#5B667A]">+{data.keywords.length - 8} more</Badge>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-[1fr_340px] gap-6">
          {/* Leads List */}
          <div>
            <div className="text-sm font-medium text-[#5B667A] mb-3">
              {data.total_leads} leads found across {data.suggested_subreddits.length} subreddits
            </div>

            {/* Visible leads */}
            <div className="space-y-3">
              {visibleLeads.map((lead, i) => (
                <Card key={i} className="p-4 rounded-xl border-[#E6E8EE] hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-[#0B0F17] line-clamp-2">{lead.title}</h3>
                      <p className="text-xs text-[#5B667A] mt-1">r/{lead.subreddit}</p>
                      <p className="text-xs text-[#9ca3af] mt-1 line-clamp-2">{(lead.body || '').replace(/submitted by\/?u\/\S+\[link\]\[comments\]/gi, '').replace(/submitted by\/?u\/\S+/gi, '').trimEnd().substring(0, 150)}...</p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <IntentBadge score={lead.intent_score} />
                      <StatusBadge status="New" />
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {/* Blurred leads */}
            {blurredLeads.length > 0 && (
              <div className="mt-3 relative">
                <div className="blur-gate space-y-3">
                  {blurredLeads.map((lead, i) => (
                    <Card key={i} className="p-4 rounded-xl border-[#E6E8EE]">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <h3 className="text-sm font-medium">{lead.title}</h3>
                          <p className="text-xs text-[#5B667A] mt-1">r/{lead.subreddit}</p>
                        </div>
                        <IntentBadge score={lead.intent_score} />
                      </div>
                    </Card>
                  ))}
                </div>
                <div className="absolute inset-0 flex items-center justify-center bg-white/40 rounded-xl">
                  <div className="text-center">
                    <Lock className="w-8 h-8 text-[#9ca3af] mx-auto mb-2" />
                    <p className="text-sm font-medium text-[#0B0F17]">+{remaining} more leads available</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Signup CTA */}
          <div className="lg:sticky lg:top-20">
            <Card className="p-6 rounded-xl border-[#E6E8EE] shadow-[0_1px_2px_rgba(16,24,40,0.06),0_12px_28px_rgba(16,24,40,0.06)]">
              <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Unlock all {data.total_leads} leads</h3>
              <p className="text-sm text-[#5B667A] mb-4">Create a free account to access the full lead feed, AI reply generator, and keyword management.</p>
              <ul className="space-y-2 mb-6">
                {['Full lead feed with filters', 'AI-powered reply drafts', 'Keyword & subreddit management', 'Intent scoring & prioritization'].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-[#0B0F17]">
                    <div className="w-4 h-4 rounded-full bg-[#E9F9EF] flex items-center justify-center flex-shrink-0">
                      <svg className="w-2.5 h-2.5 text-[#16A34A]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => navigate(user ? '/onboard' : '/signup')}
                className="w-full h-11 rounded-lg font-semibold text-sm"
                style={{ background: 'linear-gradient(180deg, #FF6A2A 0%, #FF4500 100%)' }}
                data-testid="lead-preview-signup-cta-button"
              >
                {user ? 'Set Up Dashboard' : 'Create Free Account'} <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
